import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import StaffScheduleTable from './StaffScheduleTable';
import DoctorScheduleTable from './DoctorScheduleTable';
import AllScheduleTable from './AllScheduleTable';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingOverlay from './LoadingOverlay';

interface DailyViewTabsProps {
  timeSlots: string[];
  currentDate: Date;
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialTab?: string;
  getAppointmentForScheduleItem: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem: (scheduleItemId: string) => Task | undefined;
  onCellClick: (time: string, personId: string, isDoctor?: boolean) => void;
  onEditItem: (item: ScheduleItem) => void;
  onBookAppointment: (schedule: ScheduleItem) => void;
  onViewAppointment: (appointment: Appointment) => void;
  onAssignTask: (schedule: ScheduleItem) => void;
  onViewTask: (task: Task) => void;
  onAddScheduleItem?: (item: Omit<ScheduleItem, 'id'>) => void;
  onDeleteScheduleItem?: (id: string) => void;
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
}

const DailyViewTabs = ({
  timeSlots,
  currentDate,
  scheduleItems,
  appointments,
  tasks,
  doctors,
  staff,
  rooms,
  initialTab,
  getAppointmentForScheduleItem,
  getTaskForScheduleItem,
  onCellClick,
  onEditItem,
  onBookAppointment,
  onViewAppointment,
  onAssignTask,
  onViewTask,
  onAddScheduleItem,
  onDeleteScheduleItem,
  isLoadingSchedules = false,
  isFetchingSchedules = false,
}: DailyViewTabsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTabLoading, setIsTabLoading] = useState(false);

  // Map URL-friendly tab names to actual tab values
  const tabMapping: { [key: string]: string } = {
    'all-schedule': 'all',
    'doctors': 'doctors',
    'staff': 'staff'
  };

  // Determine the default tab from the URL or props
  const getDefaultTab = () => {
    if (initialTab) {
      // Direct mapping for non-URL-friendly values
      if (initialTab === 'all' || initialTab === 'doctors' || initialTab === 'staff') {
        return initialTab;
      }
      // URL-friendly mapping
      if (tabMapping[initialTab]) {
        return tabMapping[initialTab];
      }
    }
    return 'all';
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // Update tab when URL changes
  useEffect(() => {
    if (initialTab && tabMapping[initialTab]) {
      setActiveTab(tabMapping[initialTab]);
    }
  }, [initialTab]);

  // Handle tab change
  const handleTabChange = (value: string) => {
    setIsTabLoading(true);
    setActiveTab(value);
    // Update URL with the new tab
    const reverseMapping: { [key: string]: string } = {
      'all': 'all-schedule',
      'doctors': 'doctors',
      'staff': 'staff'
    };
    navigate(`/daily/${reverseMapping[value]}`);

    // Clear loading after minimum display time
    setTimeout(() => setIsTabLoading(false), 300);
  };
  
  // Filter schedule items for the current day
  const dateStr = format(currentDate, 'yyyy-MM-dd');
  const dayScheduleItems = scheduleItems.filter(item => item.date === dateStr);
  
  // Debug logging
  console.log('[DailyViewTabs] Component props:', {
    roomsCount: rooms?.length || 0,
    roomsData: rooms,
    appointmentsCount: appointments?.length || 0,
    hasGetAppointmentFunc: !!getAppointmentForScheduleItem
  });

  // Common props for all table components
  const commonProps = {
    timeSlots,
    scheduleItems: dayScheduleItems,
    appointments,
    tasks,
    doctors,
    staff,
    rooms,
    getAppointmentForScheduleItem,
    getTaskForScheduleItem,
    onCellClick,
    onEditItem,
    onBookAppointment,
    onViewAppointment,
    onAssignTask,
    onViewTask,
    onAddScheduleItem,
    onDeleteScheduleItem,
    currentDate: dateStr,
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="overflow-x-auto -mx-2 px-2 pb-1">
          <TabsList className="grid w-fit min-w-full sm:min-w-0 sm:w-fit grid-cols-3">
            <TabsTrigger value="all" className="text-xs sm:text-sm whitespace-nowrap">All Schedules</TabsTrigger>
            <TabsTrigger value="doctors" className="text-xs sm:text-sm whitespace-nowrap">Doctors</TabsTrigger>
            <TabsTrigger value="staff" className="text-xs sm:text-sm whitespace-nowrap">Staff</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-1">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs shrink-0">Doctors</Badge>
          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0">Available</Badge>
          <Badge variant="outline" className="bg-blue-100 text-blue-800 text-xs shrink-0">Booked</Badge>
          <Badge variant="outline" className="bg-red-50 text-red-700 text-xs shrink-0">Leave/MC</Badge>
          <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs shrink-0">Admin Task</Badge>
        </div>
      </div>

      <TabsContent value="all">
        <Card>
          <CardHeader>
            <h3 className="text-base sm:text-lg font-semibold">All Schedules - {dayScheduleItems.length} items for {dateStr}</h3>
          </CardHeader>
          <CardContent>
            <AllScheduleTable
              {...commonProps}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="doctors">
        <Card>
          <CardHeader>
            <h3 className="text-base sm:text-lg font-semibold">Doctor Schedules - {dayScheduleItems.filter(item => item.doctorId).length} doctor items</h3>
          </CardHeader>
          <CardContent>
            <DoctorScheduleTable
              {...commonProps}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="staff">
        <Card>
          <CardHeader>
            <h3 className="text-base sm:text-lg font-semibold">Staff Schedules - {dayScheduleItems.filter(item => item.staffId).length} staff items</h3>
          </CardHeader>
          <CardContent>
            <StaffScheduleTable
              {...commonProps}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <LoadingOverlay
        isLoading={isLoadingSchedules || isTabLoading}
        message={isTabLoading ? "Switching tab..." : "Loading schedule..."}
      />
    </Tabs>
  );
};

export default DailyViewTabs;
